/**
 * Billing Readiness Report (Monday 12087849135).
 *
 * Cross-clinic admin report flagging clinics at risk of revenue disruption.
 * Backed by `ClinicActivityDaily` (events land in ClinicActivityEvent and an
 * hourly proc rolls them up), so every windowed figure is just a SUM of daily rows.
 */

export enum BillingReadinessSeverity {
  Critical = 'critical',
  Moderate = 'moderate',
  Normal = 'normal',
  /** No data to score against (e.g. clinic has never submitted a claim). */
  None = 'none',
}

/** Date-range hot-buttons shown above the table. Default 30. */
export type BillingReadinessWindow = 7 | 30 | 60 | 90;

export const BILLING_READINESS_WINDOWS: BillingReadinessWindow[] = [7, 30, 60, 90];
export const DEFAULT_BILLING_READINESS_WINDOW: BillingReadinessWindow = 30;

/** Days since the clinic last transmitted a claim (business days). */
export type TransmissionSignal = {
  /** periodTimestamp (midnight GMT ms) of the last submission, or null. */
  lastSubmission: number | null;
  businessDaysSince: number | null;
  severity: BillingReadinessSeverity;
};

/** A numerator/denominator ratio (rejection or denial) for the window. */
export type RatioSignal = {
  /** False for clinics with no clearinghouse integration (rejection only). */
  available: boolean;
  numerator: number;
  denominator: number;
  /** Percent rounded to one decimal, or null when not computable. */
  pct: number | null;
  severity: BillingReadinessSeverity;
};

/** Days since a last-known activity (posted payment / ERA received). */
export type LastActivitySignal = {
  /** periodTimestamp (midnight GMT ms) of the last activity, or null. */
  date: number | null;
  daysSince: number | null;
  severity: BillingReadinessSeverity;
};

export type BillingReadinessRow = {
  clinicId: number;
  clinicName: string;
  transmission: TransmissionSignal;
  rejection: RatioSignal;
  denial: RatioSignal;
  lastPayment: LastActivitySignal;
  lastEra: LastActivitySignal;
  /** Used for the default sort (most Critical signals first). */
  criticalCount: number;
  moderateCount: number;
  /** SF onboarding enrichment (latest EHR onboarding opp); null when not in SF. */
  phase: string | null;
  onboarder: string | null;
};

export type BillingReadinessResponse = {
  window: BillingReadinessWindow;
  rows: BillingReadinessRow[];
};

/** One STC01-1:STC01-2 rejection-code row in the detail modal's rejection table. */
export type RejectionCodeRow = {
  /** STC code pair, e.g. "A7:21". Empty string = rejection recorded without a code. */
  code: string;
  count: number;
  /** count / submissions-in-window * 100, rounded to one decimal, or null. */
  pct: number | null;
  /** Standard X12 STC category + status description for the code pair, if resolvable. */
  description?: string;
};

export type TransmissionDetail = {
  /** All-time CLM count (not windowed). */
  totalAllTime: number;
  /** CLM count within the selected window. */
  inWindow: number;
  lastSubmission: number | null;
  businessDaysSince: number | null;
  severity: BillingReadinessSeverity;
};

/** Per-clinic deep detail behind a row (modal). Windowed figures respect `window`. */
export type BillingReadinessDetail = {
  clinicId: number;
  clinicName: string;
  window: BillingReadinessWindow;
  transmission: TransmissionDetail;
  rejection: RatioSignal & { codes: RejectionCodeRow[] };
  /** `source`: where denials came from this window — "835 EDI" / "Manual ERA" / both. */
  denial: RatioSignal & { source: string | null };
  lastPayment: LastActivitySignal;
  /** `source`: where the most recent ERA came from — "835 EDI" / "Manual ERA". */
  lastEra: LastActivitySignal & { source: string | null };
};

/** Business days since last claim: Critical >= 21, Moderate 10-20, Normal <= 9. */
export const transmissionSeverity = (businessDays: number | null): BillingReadinessSeverity => {
  if (businessDays == null) return BillingReadinessSeverity.None;
  if (businessDays >= 21) return BillingReadinessSeverity.Critical;
  if (businessDays >= 10) return BillingReadinessSeverity.Moderate;
  return BillingReadinessSeverity.Normal;
};

/** Rejection/denial ratio: Critical >= 25%, Moderate 10-24%, Normal < 10%. */
export const ratioSeverity = (pct: number | null): BillingReadinessSeverity => {
  if (pct == null) return BillingReadinessSeverity.None;
  if (pct >= 25) return BillingReadinessSeverity.Critical;
  if (pct >= 10) return BillingReadinessSeverity.Moderate;
  return BillingReadinessSeverity.Normal;
};

/** Zero-revenue signals: Critical > 14, Moderate 5-13, Normal < 5 (calendar days). */
export const daysSinceSeverity = (days: number | null): BillingReadinessSeverity => {
  if (days == null) return BillingReadinessSeverity.None;
  if (days > 14) return BillingReadinessSeverity.Critical;
  if (days >= 5) return BillingReadinessSeverity.Moderate;
  return BillingReadinessSeverity.Normal;
};
